import { useMemo } from 'react';

/**
 * A custom React hook to format a number or number string with
 * thousands separators and optional locale support.
 *
 * @param {string | number | undefined | null} value The number or string to format.
 * @param {string} [locale='en-US'] The BCP 47 language tag for the locale (e.g., 'de-DE', 'fr-FR').
 * @param {Intl.NumberFormatOptions} [options={}] Additional formatting options.
 * @returns {string} The formatted number string (e.g., '2,145,050').
 */

const useNumberFormat = (value: string, locale = 'en-US', options = {}) => {
  // Memoize the formatted value to prevent unnecessary re-calculations
  // on every render, assuming value, locale, and options are stable.
  const formattedValue = useMemo(() => {
    // 1. Handle null, undefined, or empty string input
    if (value === null || value === undefined || value === '') {
      return '';
    }

    let numberToFormat;

    // 2. Convert string to number if necessary
    if (typeof value === 'string') {
      // Use parseFloat to handle strings like "2145050"
      numberToFormat = parseFloat(value);
    } else {
      numberToFormat = value;
    }

    // 3. Validate if the conversion resulted in a valid number
    if (isNaN(numberToFormat)) {
      // You might choose to return an error string, the original value,
      // or an empty string depending on desired behavior.
      console.error(`useFormattedNumber received an invalid number/string: ${value}`);
      return String(value); // Return the original value/string
    }

    try {
      // 4. Use Intl.NumberFormat for locale-aware formatting
      return new Intl.NumberFormat(locale, options).format(numberToFormat);
    } catch (e) {
      console.error('Error during number formatting:', e);
      // Fallback to basic toLocaleString or the original number string
      return numberToFormat.toLocaleString(locale);
    }
  }, [value, locale, options]);

  return formattedValue;
};

export default useNumberFormat;
